import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Calculator, 
  Receipt, 
  ArrowRight, 
  Sparkles,
  CheckCircle,
  Zap,
  Shield,
  Clock
} from 'lucide-react';

const ServiceCard = ({ service, index, isHovered, onHover, onLeave }) => {
  const navigateTo = (url) => {
    window.location.href = url;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={onLeave}
      onClick={() => navigateTo('https://app.meumei.net/register')}
      className="group relative cursor-pointer"
    >
      {/* Card principal */}
      <motion.div
        animate={{
          y: isHovered ? -10 : 0,
          scale: isHovered ? 1.02 : 1,
        }}
        transition={{ duration: 0.3 }}
        className="relative h-full rounded-3xl overflow-hidden"
        style={{
          background: isHovered 
            ? 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
            : 'linear-gradient(135deg, #1a4d42 0%, #2C5E50 50%, #1a4d42 100%)',
          boxShadow: isHovered
            ? '0 25px 60px rgba(16, 185, 129, 0.4)'
            : '0 15px 40px rgba(44, 94, 80, 0.3)',
        }}
      >
        {/* Efeito de brilho animado */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100"
          initial={{ x: '-100%' }}
          whileHover={{ x: '100%' }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
          }}
        />

        {/* Padrão de fundo decorativo */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)`,
              backgroundSize: '20px 20px',
            }}
          />
        </div>

        {/* Círculo decorativo */}
        <motion.div
          animate={{
            scale: isHovered ? 1.5 : 1,
            opacity: isHovered ? 0.2 : 0.1,
          }}
          transition={{ duration: 0.5 }}
          className="absolute -top-20 -right-20 w-40 h-40 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(217, 249, 157, 0.5) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 p-8 h-full flex flex-col">
          {/* Número do serviço */}
          <motion.div
            className="absolute top-4 right-4 text-6xl font-bold opacity-10 text-white"
            animate={{ opacity: isHovered ? 0.2 : 0.1 }}
          >
            0{index + 1}
          </motion.div>

          {/* Ícone */}
          <motion.div
            animate={{
              rotate: isHovered ? [0, -10, 10, 0] : 0,
              scale: isHovered ? 1.1 : 1,
            }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 relative"
            style={{
              background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
              boxShadow: '0 8px 25px rgba(217, 249, 157, 0.4)',
            }}
          >
            <service.icon className="text-[#2C5E50]" size={32} />
            
            {/* Pulse effect */}
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </motion.div>

          {/* Badge de destaque */}
          {service.badge && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold mb-4 w-fit"
              style={{
                background: 'rgba(217, 249, 157, 0.2)',
                color: '#d9f99d',
                border: '1px solid rgba(217, 249, 157, 0.3)',
              }}
            >
              <Zap size={12} />
              {service.badge}
            </motion.div>
          )}

          {/* Título */}
          <h3 className="text-xl md:text-2xl font-bold text-white mb-4 leading-tight">
            {service.title}
          </h3>

          {/* Descrição */}
          <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          {/* Lista de benefícios */}
          <div className="space-y-2 mb-6">
            {service.benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-2 text-sm text-gray-300"
              >
                <CheckCircle size={16} className="text-[#d9f99d] flex-shrink-0" />
                <span>{benefit}</span>
              </motion.div>
            ))}
          </div>

          {/* Botão CTA */}
          <motion.div
            className="flex items-center gap-2 text-[#d9f99d] font-semibold group/btn"
            animate={{ x: isHovered ? 5 : 0 }}
          >
            <span className="relative">
              Começar agora
              <motion.span
                className="absolute bottom-0 left-0 h-0.5 bg-[#d9f99d]"
                initial={{ width: 0 }}
                animate={{ width: isHovered ? '100%' : 0 }}
                transition={{ duration: 0.3 }}
              />
            </span>
            <motion.span
              animate={{ x: isHovered ? 5 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ArrowRight size={20} />
            </motion.span>
          </motion.div>
        </div>

        {/* Borda inferior colorida */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{
            background: 'linear-gradient(90deg, #d9f99d, #10b981, #d9f99d)',
          }}
          animate={{
            opacity: isHovered ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>
    </motion.div>
  );
};

const ServicesSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const services = [
    {
      icon: FileText,
      title: 'Abertura de CNPJ MEI',
      description: 'Abra seu MEI com facilidade e com todo suporte. Processo 100% online e automatizado com gestão completa.',
      badge: 'Mais Popular',
      benefits: [
        'Processo 100% digital',
        'Suporte especializado',
        'Pronto em até 24h',
      ],
    },
    {
      icon: Calculator,
      title: 'Declaração Anual (DASN)',
      description: 'Declaração Anual automática. O sistema trabalha para você, sem precisar ficar cobrando contador.',
      badge: 'Automático',
      benefits: [
        'Preenchimento automático',
        'Lembretes de prazo',
        'Sem complicação',
      ],
    },
    {
      icon: Receipt,
      title: 'Gestão Financeira e NF',
      description: 'Gerenciamento completo do seu financeiro e emissão de notas fiscais de forma simples e automática.',
      badge: 'Completo',
      benefits: [
        'Emissão de NF fácil',
        'Controle financeiro',
        'Relatórios detalhados',
      ],
    },
  ];

  const features = [
    { icon: Zap, text: 'Rápido' },
    { icon: Shield, text: 'Seguro' },
    { icon: Clock, text: '24/7' },
  ];

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background gradiente */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
        }}
      />

      {/* Elementos decorativos */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1],
          }}
          transition={{ 
            rotate: { duration: 50, repeat: Infinity, ease: 'linear' },
            scale: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
          }}
          className="absolute -top-40 -left-40 w-80 h-80 rounded-full opacity-30"
          style={{
            background: 'conic-gradient(from 0deg, #10b981, #06b6d4, #10b981)',
            filter: 'blur(60px)',
          }}
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1.1, 1, 1.1],
          }}
          transition={{ 
            rotate: { duration: 60, repeat: Infinity, ease: 'linear' },
            scale: { duration: 10, repeat: Infinity, ease: 'easeInOut' },
          }}
          className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'conic-gradient(from 180deg, #2C5E50, #10b981, #2C5E50)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header da seção */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              <motion.span
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={16} className="text-[#10b981]" />
              </motion.span>
              <span className="text-sm font-medium text-[#10b981]">
                Nossos Serviços
              </span>
            </motion.div>

            {/* Título */}
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C5E50] leading-tight mb-6">
              SERVIÇOS PRO{' '}
              <span className="bg-gradient-to-r from-[#10b981] to-[#06b6d4] bg-clip-text text-transparent">
                SEU MEI
              </span>
              <br />
              PRA QUANDO VOCÊ PRECISAR
            </h2>

            {/* Descrição */}
            <p className="text-gray-600 text-lg mb-8">
              Soluções completas para simplificar sua vida como microempreendedor. 
              Tudo em um só lugar, sem burocracia.
            </p>

            {/* Features rápidas */}
            <div className="flex flex-wrap gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md"
                >
                  <feature.icon size={18} className="text-[#10b981]" />
                  <span className="text-sm font-medium text-[#2C5E50]">{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Estatística destaque */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-7 flex justify-end"
          >
            <div 
              className="relative p-8 rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(44, 94, 80, 0.1) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <motion.div
                    className="text-5xl md:text-6xl font-bold mb-2"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #2C5E50 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Muitos
                  </motion.div>
                  <p className="text-gray-600 font-medium">MEIs confiam em nós</p>
                </div>
                <div className="h-16 w-px bg-gray-300" />
                <div className="text-center">
                  <motion.div
                    className="text-5xl md:text-6xl font-bold mb-2"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  >
                    98%
                  </motion.div>
                  <p className="text-gray-600 font-medium">Taxa de satisfação</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Grid de serviços */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={index}
              isHovered={hoveredCard === index}
              onHover={setHoveredCard}
              onLeave={() => setHoveredCard(null)}
            />
          ))}
        </div>

        {/* CTA final */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 mb-6">
            Não sabe por onde começar? Fale com nossa equipe!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = 'https://app.meumei.net/register'}
              className="px-8 py-4 rounded-full font-semibold text-white"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 10px 40px rgba(16, 185, 129, 0.35)',
              }}
            >
              Começar gratuitamente
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/contato'}
              className="px-8 py-4 rounded-full font-semibold text-[#2C5E50] border-2 border-[#2C5E50] hover:bg-[#2C5E50] hover:text-white transition-colors"
            >
              Falar com especialista
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;